import React from 'react';

import { Slot, Slottable } from '@radix-ui/react-slot';
import { XIcon } from 'lucide-react';
import { type VariantProps, tv } from 'tailwind-variants';

import { forwardRefNoClassName } from 'common/uiV3/common';

const badgeVariants = tv({
  slots: {
    base: [
      'flex gap-1 items-center',
      'px-2 py-0.5 max-w-fit',
      '!text-sm !leading-4 font-medium',
      '*:!text-sm *:!leading-4 *:!font-medium',
      'rounded-sm base-border shrink-0',
      'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
      'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
      'transition-[color,box-shadow] overflow-hidden',
      'leading-auto align-self-start truncate',
      '[&>svg]:size-3 [&>svg]:pointer-events-none [&>svg]:shrink-0',
    ],
    closeIcon: 'opacity-65 hover:opacity-100 size-3',
  },
  variants: {
    variant: {
      beta: {
        base: ['border-transparent', 'text-purple-110 bg-purple-30'],
      },
      canny: {
        base: ['border-transparent', 'text-primary bg-indigo-30'],
      },
      destructive: {
        base: ['border-transparent', 'text-red-90 bg-red-30'],
      },
      info: {
        base: ['border-transparent', 'text-gray-110 bg-gray-30 dark:bg-gray-50'],
      },
      success: {
        base: ['border-transparent', 'text-grass-110 bg-grass-30'],
      },
      warning: {
        base: ['border-transparent', 'text-yellow-110 bg-yellow-30'],
      },
      outline: {
        base: ['text-foreground', '[a&]:hover:bg-accent [a&]:hover:text-accent-foreground'],
      },
    },
  },
  defaultVariants: {
    variant: 'canny',
  },
});

export type BadgeProps = Omit<React.HTMLAttributes<HTMLSpanElement>, 'className'> &
  Omit<VariantProps<typeof badgeVariants>, 'closable'> & {
    asChild?: boolean;
    onClose?: React.DOMAttributes<HTMLDivElement>['onClick'];
  };

const Badge = forwardRefNoClassName<HTMLSpanElement, BadgeProps>(
  ({ variant, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span';
    const styles = badgeVariants({ variant });

    return (
      <Comp ref={ref} data-slot="badge" className={styles.base()} {...props}>
        <Slottable>{children}</Slottable>
        {props.onClose && (
          <div
            className="flex hover:cursor-pointer text-inherit"
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={props.onClose}>
            <XIcon className={styles.closeIcon()} />
          </div>
        )}
      </Comp>
    );
  },
);

export default Badge;
